import { getActiveToken, getTabId } from 'utils/tabAuth';

// Reads sent ahead of the code that needs them: at start-up, the moment the
// app file runs; for a chat, on the press that precedes the click. The code
// that needs each answer is unchanged: its first `api.get` for the same path is
// handed the request already in flight (see the adapter in app/api.ts).

interface EarlyAnswer {
  token: string;
  expiresAt: number;
  answer: Promise<unknown>;
}

const early = new Map<string, EarlyAnswer>();

export const startEarlyRequests = (
  baseURL: string,
  paths: string[],
  freshForMs = 15_000,
): void => {
  const token = getActiveToken();
  if (!token || typeof fetch !== 'function') return;

  for (const path of paths) {
    const pending = early.get(path);
    if (pending?.token === token && Date.now() < pending.expiresAt) continue;

    const answer = fetch(`${baseURL}${path}`, {
      headers: {
        Accept: 'application/json',
        Authorization: token,
        'x-edit-session-id': getTabId(),
      },
    }).then(response => {
      if (!response.ok) throw new Error(String(response.status));
      return response.json() as Promise<unknown>;
    });
    // Whoever takes it handles a failure, by asking again the usual way.
    answer.catch(() => undefined);

    const entry = { token, expiresAt: Date.now() + freshForMs, answer };
    early.set(path, entry);
    window.setTimeout(() => {
      if (early.get(path) === entry) early.delete(path);
    }, freshForMs);
  }
};

/** The answer in flight for `path`: handed out once, only while fresh, only to the login that asked. */
export const takeEarlyAnswer = (path: string): Promise<unknown> | null => {
  const entry = early.get(path);
  if (!entry) return null;
  early.delete(path);
  const usable =
    entry.token === getActiveToken() && Date.now() < entry.expiresAt;
  return usable ? entry.answer : null;
};
